import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Smartphone, BadgeCheck, Play, Heart, Eye } from "lucide-react";

export const Hero = ({ onCreate }) => {
  const [url, setUrl] = useState("");

  return (
    <section id="top" className="relative overflow-hidden itm-grid-bg pt-28 pb-20 lg:pt-36 lg:pb-28">
      <div className="absolute inset-0 itm-dots opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-8 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                      className="inline-flex items-center gap-2 rounded-full border border-[#00F5D4]/30 bg-[#00F5D4]/5 px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F5D4] animate-pulse" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#00F5D4]">As seen on Instagram</span>
          </motion.div>

          <motion.h1 data-testid="hero-headline"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Turn any product link into a
            <span className="text-[#FF2E63]"> viral Instagram video</span> in 24 hours.
          </motion.h1>

          <motion.p data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
            className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
            Drop your product or service page, add your brand vibe, and get a scroll-stopping 9:16 reel delivered to your inbox — ready to post. No editors, no briefs, no waiting.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}
            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
            <input
              data-testid="hero-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste your product page URL…"
              className="flex-1 rounded-full bg-[#12141C] border border-white/10 px-5 py-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#FF2E63]/60 transition-colors"
            />
            <button data-testid="hero-cta-submit" onClick={() => onCreate(url)}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#FF2E63] hover:bg-[#E02554] text-white font-semibold px-6 py-3.5 transition-colors whitespace-nowrap">
              Make it viral <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.26 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-400">
            {[
              { icon: Zap, label: "24-hour delivery" },
              { icon: Smartphone, label: "9:16 Instagram native" },
              { icon: BadgeCheck, label: "Full rights included" },
            ].map((b) => (
              <span key={b.label} className="inline-flex items-center gap-2">
                <b.icon className="w-4 h-4 text-[#00F5D4]" /> {b.label}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Phone mockup */}
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="relative mx-auto" data-testid="hero-phone-preview">
          <div className="itm-float relative w-[260px] sm:w-[300px] aspect-[9/16] rounded-[2.2rem] border-[6px] border-[#1A1D29] bg-[#12141C] shadow-2xl shadow-[#FF2E63]/10 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1543525469-65b61cc2bc06?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                 alt="Viral reel preview" className="absolute inset-0 w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
            <div className="absolute top-3 inset-x-3 flex items-center justify-between text-[10px] text-white/80 font-mono">
              <span>REEL · 00:14</span><span className="text-[#00F5D4]">● LIVE</span>
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-14 h-14 rounded-full bg-white/15 backdrop-blur-md border border-white/20">
              <Play className="w-6 h-6 text-white fill-white" />
            </div>
            <div className="absolute bottom-4 inset-x-4">
              <p className="text-white text-sm font-semibold leading-snug">Your product, but make it unskippable ✨</p>
              <div className="mt-3 flex items-center gap-4 text-white/90 text-xs">
                <span className="inline-flex items-center gap-1"><Heart className="w-4 h-4 text-[#FF2E63] fill-[#FF2E63]" /> 48.2k</span>
                <span className="inline-flex items-center gap-1"><Eye className="w-4 h-4" /> 1.2M</span>
              </div>
            </div>
          </div>
          <div className="absolute -right-4 top-10 rounded-xl bg-[#00F5D4] text-[#0A0B10] text-xs font-bold px-3 py-2 shadow-lg itm-float" style={{ animationDelay: "1.5s" }}>
            +312% reach
          </div>
        </motion.div>
      </div>
    </section>
  );
};
